import { FileEvent } from '@components/buttons/BrowseFilesButton';
import DatePicker from '@components/inputs/DateInput';
import DropDownMenu from '@components/inputs/DropDownMenu';
import Label from '@components/inputs/text/Label';
import UploadFilePanel from '@components/panels/UploadFile/UploadFilePanel';
import { DocumentConfig, getDocumentConfig, IdentityDocumentType } from '@services/identityVerification/IdentityVerification.types';
import { Country } from '@utils/constants/localisation';
import { dateToISOFormat } from '@utils/misc/functions';
import { textBoxStyle } from '@utils/styles/textboxStyle';
import React, { Dispatch, useEffect } from 'react';
import { DocumentDetail, DocumentDetailAction } from '../../views/IdentityVerificationDocumentUpload/documentDetailReducer';
import { FILE_ACCEPT_TYPES } from '../../views/IdentityVerificationDocumentUpload/validateDocumentDetail';

const DEFAULT_DOCUMENT_CONFIG: DocumentConfig = {
    includedFields: [],
    requiredFields: [],
    requiresThreeMonthExpiry: false,
};

const AUSTRALIAN_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'VIC', 'TAS', 'WA'];

// Field labels for both UI rendering and validation error messages
const FIELD_LABELS: Record<keyof DocumentDetail, string> = {
    documentType: 'Document Type',
    documentNumber: 'Document Number',
    expiryDate: 'Expiry Date',
    dateOfBirth: 'Date of Birth',
    state: 'Licence State',
    nationality: 'Nationality',
    issueDate: 'Issue Date',
    streetLine1: 'Street Line 1',
    streetLine2: 'Street Line 2',
    city: 'City',
    postCode: 'Post Code',
    country: 'Country',
    frontFile: 'Front Image',
    backFile: 'Back Image',
    tripsCompleted: 'Trips Completed',
    uberTenure: 'Uber Tenure',
    drivingHoursPerWeek: 'Hours Driven per Week',
    uberRating: 'Uber Rating'
};

/**
 * Gets the human-readable label for a field
 */
export function getFieldLabel(field: keyof DocumentDetail, documentType: IdentityDocumentType | null): string {
    // Driver Licence has a different label for document number
    if (field === 'documentNumber' && documentType === IdentityDocumentType.DRIVER_LICENCE) {
        return 'Driver Licence Number';
    }

    // PCO has custom labels
    if (documentType === IdentityDocumentType.PCO) {
        if (field === 'documentNumber') return 'PCO Licence Number';
        if (field === 'frontFile') return 'PCO Licence Front Image';
        if (field === 'backFile') return 'PCO Badge Front Image';
    }

    return FIELD_LABELS[field];
}

interface IdentityDocumentSectionProps {
    title: string;
    availableDocumentTypes: IdentityDocumentType[];
    documentDetail: DocumentDetail;
    documentDetailDispatch: Dispatch<DocumentDetailAction>;
    country: Country;
    testId?: string;
}

// Add a three-month-ish gap (matching BE validation) if necessary, otherwise minimum is tomorrow.
export function calculateMinimumExpiryDate(currentDate: Date, requiresThreeMonthExpiry: boolean): string {
    if (requiresThreeMonthExpiry) {
        return dateToISOFormat(new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate() + 91
        ));
    } else {
        return dateToISOFormat(new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate() + 1
        ));
    }
}

// A 10-year document obtained on the same day as onboarding will have an expiry date 10 years + 1 day in the future.
// As a rough common-sense check, this is the latest expiry date we expect to encounter for any supported documents.
export function calculateMaximumExpiryDate(currentDate: Date): string {
    return dateToISOFormat(new Date(
        currentDate.getFullYear() + 10,
        currentDate.getMonth(),
        currentDate.getDate() + 1
    ));
}

// Driver's date of birth must be at least 23 years ago
export function calculateMaximumDateOfBirth(currentDate: Date): string {
    return dateToISOFormat(new Date(
        currentDate.getFullYear() - 23,
        currentDate.getMonth(),
        currentDate.getDate()
    ));
}

// Proof of Address must be issued more recently than 3 months ago
export function calculateMinimumIssueDate(currentDate: Date): string {
    return dateToISOFormat(new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate() - 89
    ));
}

export default function DocumentPanel({
    title,
    availableDocumentTypes,
    documentDetail,
    documentDetailDispatch,
    country,
    testId
}: IdentityDocumentSectionProps) {
    const handleChangeDocumentType = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const docType = event.target.value

        // Reset to keep UI and reducer state in sync
        documentDetailDispatch({ type: 'RESET' })

        documentDetailDispatch({
            type: 'UPDATE_FIELD',
            payload: {
                field: 'documentType',
                value: docType,
            },
        })
    };

    const handleTextInputChange = (field: keyof DocumentDetail) => (event: React.ChangeEvent<HTMLInputElement>) => {
        documentDetailDispatch({
            type: 'UPDATE_FIELD',
            payload: {
                field,
                value: event.target.value,
            },
        });
    };

    // Handled just the same as above
    const handleDateInputChange = handleTextInputChange;

    const handleSelectChange = (field: keyof DocumentDetail) => (event: React.ChangeEvent<HTMLSelectElement>) => {
        documentDetailDispatch({
            type: 'UPDATE_FIELD',
            payload: {
                field,
                value: event.target.value,
            },
        });
    };

    const handleFileChange = (field: keyof DocumentDetail) => (event: FileEvent) => {
        documentDetailDispatch({
            type: 'UPDATE_FIELD',
            payload: {
                field,
                value: event.file,
            },
        });
    };

    const selectedDocumentType = documentDetail.documentType;
    const { includedFields, requiredFields, requiresThreeMonthExpiry } = selectedDocumentType ?
        getDocumentConfig(selectedDocumentType, country) :
        DEFAULT_DOCUMENT_CONFIG;

    const currentDate = new Date();
    // ID Documents' earliest expiry date might be today or 3 months from now, depending on the type.
    const earliestExpiryDate = calculateMinimumExpiryDate(currentDate, requiresThreeMonthExpiry);
    const latestExpiryDate = calculateMaximumExpiryDate(currentDate);
    // Driver's date of birth must be at least 23 years ago. Zoho doesn't allow DOBs before 1900
    const earliestDriverDOB = "1900-01-01";
    const latestDriverDOB = calculateMaximumDateOfBirth(currentDate);
    // Proof of Address must be issued more recently than 3 months ago
    const earliestPOAIssueDate = calculateMinimumIssueDate(currentDate);

    // Auto-select document type if there's only one option and none is selected
    useEffect(() => {
        if (availableDocumentTypes.length === 1 && !selectedDocumentType) {
            documentDetailDispatch({
                type: 'UPDATE_FIELD',
                payload: {
                    field: 'documentType',
                    value: availableDocumentTypes[0],
                },
            });
        }
    }, [availableDocumentTypes, selectedDocumentType, documentDetailDispatch]);

    return (
        <div data-testid={testId}>
            <div style={{ marginTop: '2vh', fontSize: '20px', fontWeight: 'bold' }}>
                {title}
            </div>

            {/* Only show dropdown if multiple document types are available, otherwise default to the only choice */}
            {availableDocumentTypes.length > 1 && (
                <div style={{ marginTop: '2vh' }}>
                    <Label text={FIELD_LABELS.documentType} requiredFieldInd={requiredFields.includes('documentType')} />
                    <DropDownMenu
                        menuName='document-type'
                        testId='document-type-dropdown'
                        required={requiredFields.includes('documentType')}
                        choices={availableDocumentTypes.map(type => ({ value: type, enabled: true }))}
                        defaultVal={selectedDocumentType ? { value: selectedDocumentType, enabled: true } : undefined}
                        onSelect={handleChangeDocumentType}
                    />
                </div>
            )}

            {selectedDocumentType && (
                <>
                    {includedFields.includes('documentNumber') && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text={getFieldLabel('documentNumber', selectedDocumentType)} requiredFieldInd={requiredFields.includes('documentNumber')} />
                            <input
                                type='text'
                                name='documentNumber'
                                data-testid='document-number-input'
                                required={requiredFields.includes('documentNumber')}
                                style={textBoxStyle}
                                onChange={handleTextInputChange('documentNumber')}
                            />
                        </div>
                    )}

                    {includedFields.includes('state') && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text={FIELD_LABELS.state} requiredFieldInd={requiredFields.includes('state')} />
                            <DropDownMenu
                                key={`state-${selectedDocumentType}`}
                                menuName='state'
                                testId='state-dropdown'
                                required={requiredFields.includes('state')}
                                choices={AUSTRALIAN_STATES.map(state => ({ value: state, enabled: true }))}
                                onSelect={handleSelectChange('state')}
                            />
                        </div>
                    )}

                    {includedFields.includes('nationality') && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text={FIELD_LABELS.nationality} requiredFieldInd={requiredFields.includes('nationality')} />
                            <input
                                type='text'
                                name='nationality'
                                data-testid='nationality-input'
                                required={requiredFields.includes('nationality')}
                                style={textBoxStyle}
                                onChange={handleTextInputChange('nationality')}
                            />
                        </div>
                    )}

                    {includedFields.includes('expiryDate') && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text={FIELD_LABELS.expiryDate} requiredFieldInd={requiredFields.includes('expiryDate')} />
                            <DatePicker
                                key={`expiryDate-${selectedDocumentType}`}
                                name='expiryDate'
                                required={requiredFields.includes('expiryDate')}
                                min={earliestExpiryDate}
                                max={latestExpiryDate}
                                onChange={handleDateInputChange('expiryDate')}
                            />
                        </div>
                    )}

                    {includedFields.includes('dateOfBirth') && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text={FIELD_LABELS.dateOfBirth} requiredFieldInd={requiredFields.includes('dateOfBirth')} />
                            <DatePicker
                                key={`dateOfBirth-${selectedDocumentType}`}
                                name='dateOfBirth'
                                required={requiredFields.includes('dateOfBirth')}
                                min={earliestDriverDOB}
                                max={latestDriverDOB}
                                onChange={handleDateInputChange('dateOfBirth')}
                            />
                        </div>
                    )}

                    {includedFields.includes('issueDate') && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text={FIELD_LABELS.issueDate} requiredFieldInd={requiredFields.includes('issueDate')} />
                            <DatePicker
                                key={`issueDate-${selectedDocumentType}`}
                                name='issueDate'
                                required={requiredFields.includes('issueDate')}
                                min={earliestPOAIssueDate}
                                onChange={handleDateInputChange('issueDate')}
                            />
                        </div>
                    )}

                    {includedFields.includes('streetLine1') && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text={FIELD_LABELS.streetLine1} requiredFieldInd={requiredFields.includes('streetLine1')} />
                            <input
                                type='text'
                                name='streetLine1'
                                data-testid='street-line1-input'
                                required={requiredFields.includes('streetLine1')}
                                style={textBoxStyle}
                                onChange={handleTextInputChange('streetLine1')}
                            />
                        </div>
                    )}

                    {includedFields.includes('streetLine2') && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text={FIELD_LABELS.streetLine2} />
                            <input
                                type='text'
                                name='streetLine2'
                                data-testid='street-line2-input'
                                style={textBoxStyle}
                                onChange={handleTextInputChange('streetLine2')}
                            />
                        </div>
                    )}

                    {includedFields.includes('city') && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text={FIELD_LABELS.city} requiredFieldInd={requiredFields.includes('city')} />
                            <input
                                type='text'
                                name='city'
                                data-testid='city-input'
                                required={requiredFields.includes('city')}
                                style={textBoxStyle}
                                onChange={handleTextInputChange('city')}
                            />
                        </div>
                    )}

                    {includedFields.includes('postCode') && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text={FIELD_LABELS.postCode} requiredFieldInd={requiredFields.includes('postCode')} />
                            <input
                                type='text'
                                name='postCode'
                                data-testid='post-code-input'
                                required={requiredFields.includes('postCode')}
                                style={textBoxStyle}
                                onChange={handleTextInputChange('postCode')}
                            />
                        </div>
                    )}

                    {includedFields.includes('country') && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text={FIELD_LABELS.country} requiredFieldInd={requiredFields.includes('country')} />
                            <input
                                type='text'
                                name='country'
                                data-testid='country-input'
                                required={requiredFields.includes('country')}
                                style={textBoxStyle}
                                onChange={handleTextInputChange('country')}
                            />
                        </div>
                    )}

                    {includedFields.includes('frontFile') && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text={getFieldLabel('frontFile', selectedDocumentType)}
                                requiredFieldInd={requiredFields.includes('frontFile')} />
                            <UploadFilePanel
                                file={documentDetail.frontFile || undefined}
                                allowedTypes={FILE_ACCEPT_TYPES}
                                required={requiredFields.includes('frontFile')}
                                testId='front-file'
                                onSelect={handleFileChange('frontFile')}
                            />
                        </div>
                    )}

                    {includedFields.includes('backFile') && (
                        <div style={{ marginTop: '2vh' }}>
                            <Label text={getFieldLabel('backFile', selectedDocumentType)}
                                requiredFieldInd={requiredFields.includes('backFile')} />
                            <UploadFilePanel
                                file={documentDetail.backFile || undefined}
                                allowedTypes={FILE_ACCEPT_TYPES}
                                required={requiredFields.includes('backFile')}
                                testId='back-file'
                                onSelect={handleFileChange('backFile')}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
